/**
 * File della rubrica della posta (539, richiesta di Davide del 2/11/2026): importazione da vCard 3.0 e 4.0 e da
 * CSV (Gmail, Google Contatti, Outlook anche in italiano), esportazione in vCard 3.0 e in CSV.
 *
 * L'importazione si fa nel browser (pagine/posta/Rubrica.tsx): il file resta sul computer e all'API arrivano
 * solo indirizzo e nome di ciascuna voce. L'esportazione la compone l'API (rotte/posta-rubrica.ts), che ne
 * scrive l'evento. Le stesse funzioni nei due lati: un file esportato si reimporta uguale.
 *
 * Si legge quello che serve alla rubrica, cioe' indirizzi email e nome. Un contatto con piu' indirizzi
 * diventa piu' voci con lo stesso nome; foto, telefoni e indirizzi postali si ignorano.
 */

export type VoceFile = {
  indirizzo: string
  nome: string | null
  /** CATEGORIES della vCard, «Labels» o «Group Membership» di Google, «Categories» di Outlook. */
  gruppi: string[]
}

export type VoceDaEsportare = { indirizzo: string; nome: string | null; gruppi: string[] }

const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/

/** Indirizzo in minuscolo, senza «mailto:» e parentesi angolari, o null se non e' un indirizzo. */
export function indirizzoValido(valore: string): string | null {
  const v = valore.trim().replace(/^mailto:/i, '').replace(/^<|>$/g, '').trim().toLowerCase()
  return EMAIL.test(v) && v.length <= 254 ? v : null
}

const pulisci = (s: string | null | undefined) => {
  const t = (s ?? '').replace(/\s+/g, ' ').trim()
  return t ? t.slice(0, 200) : null
}

// --- riconoscimento -------------------------------------------------------------------------------------

export type FormatoFile = 'vcf' | 'csv'

/** vCard se il testo comincia con BEGIN:VCARD (dopo spazi e BOM), altrimenti CSV. */
export function formatoDi(testo: string): FormatoFile {
  return /^\s*BEGIN:VCARD/i.test(testo.replace(/^\uFEFF/, '')) ? 'vcf' : 'csv'
}

export function leggiFile(testo: string): VoceFile[] {
  return formatoDi(testo) === 'vcf' ? leggiVcard(testo) : leggiCsv(testo)
}

// --- vCard ----------------------------------------------------------------------------------------------

/** Valore di vCard senza le sequenze di escape (RFC 6350 3.4): \n, \, \; \\. */
function senzaEscape(v: string): string {
  return v.replace(/\\([nN,;\\])/g, (_, c: string) => (c === 'n' || c === 'N' ? ' ' : c))
}

/** Divide sui separatori non preceduti da una barra rovesciata. */
function dividi(v: string, separatore: ',' | ';'): string[] {
  const parti: string[] = []
  let corrente = ''
  for (let i = 0; i < v.length; i++) {
    const c = v[i]!
    if (c === '\\' && i + 1 < v.length) { corrente += c + v[i + 1]; i++; continue }
    if (c === separatore) { parti.push(corrente); corrente = ''; continue }
    corrente += c
  }
  parti.push(corrente)
  return parti
}

/** Quoted-printable delle vCard 2.1 (Outlook, vecchi telefoni): accettato anche se non richiesto. */
function daQuotedPrintable(v: string): string {
  const byte: number[] = []
  const s = v.replace(/=\r?\n/g, '')
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '=' && /^[0-9A-F]{2}$/i.test(s.slice(i + 1, i + 3))) { byte.push(parseInt(s.slice(i + 1, i + 3), 16)); i += 2 } else byte.push(s.charCodeAt(i) & 0xff)
  }
  return new TextDecoder().decode(new Uint8Array(byte))
}

/** vCard 3.0 e 4.0 (anche 2.1): FN, o N; EMAIL, anche con il gruppo di Apple (item1.EMAIL); CATEGORIES. */
export function leggiVcard(testo: string): VoceFile[] {
  // Righe piegate: CRLF seguito da uno spazio o da una tabulazione (RFC 6350 3.2).
  const righe = testo.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').replace(/\n[ \t]/g, '').split('\n')
  const voci: VoceFile[] = []
  let scheda: { fn: string | null; n: string | null; email: string[]; gruppi: string[] } | null = null
  for (const riga of righe) {
    const due = riga.indexOf(':')
    if (due < 0) continue
    const testa = riga.slice(0, due)
    let valore = riga.slice(due + 1)
    const [nomeConGruppo, ...parametri] = testa.split(';')
    const nome = nomeConGruppo!.replace(/^[^.]*\./, '').toUpperCase()
    if (parametri.some((p) => /^ENCODING=QUOTED-PRINTABLE$/i.test(p))) valore = daQuotedPrintable(valore)
    if (nome === 'BEGIN' && /^VCARD$/i.test(valore.trim())) { scheda = { fn: null, n: null, email: [], gruppi: [] }; continue }
    if (!scheda) continue
    if (nome === 'END' && /^VCARD$/i.test(valore.trim())) {
      const n = scheda.n ? dividi(scheda.n, ';').map(senzaEscape).map((x) => x.trim()) : []
      // N: cognome;nome;altri nomi;prefisso;suffisso.
      const daN = [n[3], n[1], n[2], n[0], n[4]].filter((x) => x).join(' ')
      const nomeVoce = pulisci(scheda.fn) ?? pulisci(daN)
      for (const e of scheda.email) {
        const indirizzo = indirizzoValido(e)
        voci.push(indirizzo ? { indirizzo, nome: nomeVoce, gruppi: scheda.gruppi } : { indirizzo: e.trim(), nome: nomeVoce, gruppi: [] })
      }
      scheda = null
      continue
    }
    if (nome === 'FN') scheda.fn = senzaEscape(valore)
    else if (nome === 'N') scheda.n = valore
    else if (nome === 'EMAIL') scheda.email.push(senzaEscape(valore))
    else if (nome === 'CATEGORIES') {
      scheda.gruppi.push(...dividi(valore, ',').map(senzaEscape).map((g) => g.trim()).filter(Boolean))
    }
  }
  return voci
}

/** Valore di vCard con le sequenze di escape (RFC 6350 3.4). */
function conEscape(v: string): string {
  return v.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\r?\n/g, '\\n')
}

/** Righe oltre 75 ottetti piegate con CRLF e uno spazio, senza spezzare un carattere UTF-8. */
function piega(riga: string): string {
  const fuori: string[] = []
  let corrente = ''
  let ottetti = 0
  for (const c of riga) {
    const n = new TextEncoder().encode(c).length
    if (ottetti + n > (fuori.length ? 74 : 75)) { fuori.push(corrente); corrente = ''; ottetti = 0 }
    corrente += c
    ottetti += n
  }
  fuori.push(corrente)
  return fuori.join('\r\n ')
}

/** Nome e cognome da un nome intero: l'ultima parola e' il cognome, come fanno Outlook e Google. */
export function separaNome(nome: string | null): { nome: string; cognome: string } {
  const parole = (nome ?? '').trim().split(/\s+/).filter(Boolean)
  if (parole.length < 2) return { nome: parole[0] ?? '', cognome: '' }
  return { nome: parole.slice(0, -1).join(' '), cognome: parole[parole.length - 1]! }
}

/** vCard 3.0: la leggono Outlook, Gmail, Apple, Thunderbird e i telefoni. FN e N sono obbligatori. */
export function scriviVcard(voci: VoceDaEsportare[]): string {
  const schede = voci.map((v) => {
    const { nome, cognome } = separaNome(v.nome)
    const righe = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${conEscape(v.nome ?? v.indirizzo)}`,
      `N:${conEscape(cognome)};${conEscape(nome)};;;`,
      `EMAIL;TYPE=INTERNET:${v.indirizzo}`,
      ...(v.gruppi.length ? [`CATEGORIES:${v.gruppi.map(conEscape).join(',')}`] : []),
      'END:VCARD',
    ]
    return righe.map(piega).join('\r\n')
  })
  return schede.length ? `${schede.join('\r\n')}\r\n` : ''
}

// --- CSV ------------------------------------------------------------------------------------------------

/** Separatore della prima riga: virgola (Gmail), punto e virgola (Excel e Outlook in italiano) o tabulazione. */
function separatoreDi(intestazione: string): string {
  const conta = (c: string) => intestazione.split(c).length - 1
  const [, migliore] = [[conta(','), ','], [conta(';'), ';'], [conta('\t'), '\t']]
    .sort((a, b) => (b[0] as number) - (a[0] as number))[0] as [number, string]
  return migliore
}

/** Righe e campi di un CSV (RFC 4180): virgolette doppie, a capo dentro le virgolette. */
export function righeCsv(testo: string): string[][] {
  const t = testo.replace(/^\uFEFF/, '')
  const primaRiga = t.slice(0, t.search(/\r?\n|$/))
  const sep = separatoreDi(primaRiga)
  const righe: string[][] = []
  let riga: string[] = []
  let campo = ''
  let virgolette = false
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!
    if (virgolette) {
      if (c === '"' && t[i + 1] === '"') { campo += '"'; i++ } else if (c === '"') virgolette = false
      else campo += c
      continue
    }
    if (c === '"') virgolette = true
    else if (c === sep) { riga.push(campo); campo = '' } else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++
      riga.push(campo); campo = ''
      if (riga.some((x) => x.trim())) righe.push(riga)
      riga = []
    } else campo += c
  }
  riga.push(campo)
  if (riga.some((x) => x.trim())) righe.push(riga)
  return righe
}

/** Intestazione ridotta a lettere e cifre minuscole, per riconoscere le colonne di Gmail e di Outlook. */
const chiave = (s: string) => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const NOME_INTERO = new Set(['name', 'nome', 'nome completo', 'full name'])
/** Il nome mostrato di Outlook, spesso «Anna Rossi (anna@ente.it)»: vale solo senza nome e cognome. */
const NOME_MOSTRATO = new Set(['display name', 'nome visualizzato', 'e mail display name',
  'nome visualizzato posta elettronica'])
const NOME = new Set(['first name', 'given name', 'nome'])
const SECONDO = new Set(['middle name', 'additional name', 'secondo nome'])
const COGNOME = new Set(['last name', 'family name', 'surname', 'cognome'])
const GRUPPI = new Set(['labels', 'group membership', 'categories', 'categorie', 'etichette'])

/** Colonna di un indirizzo: «E-mail 1 - Value», «E-mail Address», «E-mail 2 Address», «Indirizzo posta elettronica». */
function colonnaEmail(c: string): boolean {
  if (/display|visualizzato|type|label|tipo/.test(c)) return false
  return /^e ?mail( \d+)?( value| address)?$/.test(c) || /^e ?mail \d+ (value|address)$/.test(c)
    || /^e ?mail address( \d+)?$/.test(c) || /^indirizzo (di )?posta elettronica( \d+)?$/.test(c)
    || /^(primary |secondary |other )?email$/.test(c)
}

/**
 * CSV di Gmail (Google Contatti, vecchio e nuovo), di Outlook (anche in italiano, con il punto e virgola) e i
 * fogli fatti a mano con una colonna di indirizzi. Una voce per indirizzo. Le «Labels» di Google come
 * «* myContacts ::: Lavoro» diventano i gruppi senza quelli di sistema (che cominciano con *).
 */
export function leggiCsv(testo: string): VoceFile[] {
  const righe = righeCsv(testo)
  if (!righe.length) return []
  const testa = righe[0]!.map(chiave)
  const email = testa.map((c, i) => (colonnaEmail(c) ? i : -1)).filter((i) => i >= 0)
  const trova = (insieme: Set<string>) => testa.findIndex((c) => insieme.has(c))
  // «Nome» e' il nome intero in un foglio a mano, il nome di battesimo accanto a «Cognome» (Outlook in italiano).
  const conCognome = trova(COGNOME) >= 0
  const intero = testa.findIndex((c) => NOME_INTERO.has(c) && !(conCognome && c === 'nome'))
  const [nome, secondo, cognome, gruppi] = [trova(NOME), trova(SECONDO), trova(COGNOME), trova(GRUPPI)]
  const mostrato = trova(NOME_MOSTRATO)
  const dati = (email.length ? righe.slice(1) : righe)
    // L'apostrofo che scriviCsv mette davanti a = + - @ (per Excel) non fa parte del valore.
    .map((r) => r.map((c) => c.replace(/^'(?=[=+\-@])/, '')))
  const voci: VoceFile[] = []
  for (const r of dati) {
    const composto = [nome, secondo, cognome].filter((i) => i >= 0).map((i) => (r[i] ?? '').trim()).filter(Boolean).join(' ')
    const nomeVoce = pulisci(intero >= 0 ? r[intero] : null) ?? pulisci(composto)
      ?? pulisci(mostrato >= 0 ? (r[mostrato] ?? '').replace(/\s*\([^()]*@[^()]*\)\s*$/, '') : null)
    const etichette = gruppi >= 0
      ? (r[gruppi] ?? '').split(/\s*(?::::|;|,)\s*/).map((g) => g.trim()).filter((g) => g && !g.startsWith('*'))
      : []
    // Senza intestazione riconosciuta: ogni cella che e' un indirizzo.
    const celle = email.length ? email.map((i) => r[i] ?? '') : r
    for (const cella of celle) {
      // Google mette piu' indirizzi nella stessa cella separati da « ::: ».
      for (const parte of cella.split(/\s*:::\s*/)) {
        if (!parte.trim()) continue
        const indirizzo = indirizzoValido(parte)
        if (!email.length && !indirizzo) continue
        voci.push(indirizzo ? { indirizzo, nome: nomeVoce, gruppi: etichette } : { indirizzo: parte.trim(), nome: nomeVoce, gruppi: [] })
      }
    }
  }
  return voci
}

/** Campo CSV fra virgolette quando serve; un valore che comincia con = + - @ prende un apostrofo (Excel). */
function campoCsv(v: string): string {
  const sicuro = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v
  return /[",;\r\n]/.test(sicuro) ? `"${sicuro.replace(/"/g, '""')}"` : sicuro
}

/**
 * CSV con le colonne di Outlook, che Gmail e Outlook importano entrambi e Excel apre. Separatore virgola,
 * UTF-8 con il BOM perche' Excel riconosca le lettere accentate.
 */
export function scriviCsv(voci: VoceDaEsportare[]): string {
  const righe = [['First Name', 'Last Name', 'E-mail Address', 'E-mail Display Name', 'Categories']]
  for (const v of voci) {
    const { nome, cognome } = separaNome(v.nome)
    righe.push([nome, cognome, v.indirizzo, v.nome ?? '', v.gruppi.join(';')])
  }
  return `\uFEFF${righe.map((r) => r.map(campoCsv).join(',')).join('\r\n')}\r\n`
}
